import React, { memo, useEffect, useMemo, useRef, useState } from "react";

// Charts for the full-page timeline: the pot growing through the night
// (stacked by player) and a vertical "metro map" with one line per player.
// Both share one scrub time, so tapping either shows the same moment in both.

const clock = (t) => new Date(t).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
// Round marks: "10 PM" on the hour, a bare "10:30" between.
const hourLabel = (t) => {
  const d = new Date(t);
  if (!d.getMinutes()) return d.toLocaleTimeString([], { hour: "numeric" });
  return clock(t).replace(/\s?[APap]\.?[Mm]\.?$/, "");
};

// Eight categorical slots (validated light and dark in timeline-detail.css),
// in fixed order. A ninth player and beyond fold into "Others".
const SLOTS = 8;
export const slotColor = (slot) => (slot === null ? "var(--tld-other)" : `var(--tld-s${slot + 1})`);

// The night as chart data. Players are ordered by money in (then seat time),
// which fixes their colour slot for both charts and the legend. Saved buy-in
// counts are the truth; rebuy events only place them in time (extra events
// drop oldest first, like the lanes; missing ones count from the seat).
export function buildNight({ events, playerIds, buyIns, buyInAmount, startedAt, endedAt, getName }) {
  const amount = buyInAmount || 0;
  const list = playerIds.map((pid) => {
    const own = events.filter((ev) => ev.pid === pid);
    const seat = own.find((ev) => ev.type === "buyin")?.t ?? startedAt;
    const count = Math.max(1, buyIns?.[pid] || 1);
    const rebuys = own.filter((ev) => ev.type === "rebuy").map((ev) => ev.t);
    while (rebuys.length > count - 1) rebuys.shift();
    const missing = count - 1 - rebuys.length;
    // Seated stretches: seat (or back in) to out (or the end).
    const segments = [];
    let from = seat;
    const outs = [];
    const backs = [];
    for (const ev of own) {
      if (ev.type === "out" && from !== null) { segments.push([from, ev.t]); outs.push(ev.t); from = null; }
      else if (ev.type === "back" && from === null) { from = ev.t; backs.push(ev.t); }
    }
    if (from !== null) segments.push([from, endedAt]);
    const steps = [{ t: seat, add: amount * (1 + missing) }, ...rebuys.map((t) => ({ t, add: amount }))];
    return { pid, name: getName(pid) || "Unknown player", seat, count, total: count * amount, rebuys, outs, backs, segments, steps, inAtEnd: from !== null };
  });
  list.sort((a, b) => (b.total - a.total) || (a.seat - b.seat));
  const fold = list.length > SLOTS;
  list.forEach((p, i) => { p.slot = fold && i >= SLOTS - 1 ? null : i; });

  // Money in at time t, per stack band (players, then one "Others" band).
  const bands = list.filter((p) => p.slot !== null).map((p) => ({ key: p.pid, name: p.name, slot: p.slot, steps: p.steps, total: p.total }));
  if (fold) {
    const rest = list.filter((p) => p.slot === null);
    bands.push({ key: "others", name: `${rest.length} others`, slot: null, steps: rest.flatMap((p) => p.steps), total: rest.reduce((s, p) => s + p.total, 0) });
  }
  const valueAt = (band, t) => band.steps.reduce((s, st) => (st.t <= t ? s + st.add : s), 0);
  const times = [...new Set([startedAt, endedAt, ...list.flatMap((p) => p.steps.map((s) => Math.min(Math.max(s.t, startedAt), endedAt)))])].sort((a, b) => a - b);
  const pot = list.reduce((s, p) => s + p.total, 0);

  // Every moment worth snapping to, newest event last at each time.
  const moments = [];
  for (const p of list) {
    moments.push({ t: p.seat, text: `${p.name} sat down` });
    p.rebuys.forEach((t) => moments.push({ t, text: `${p.name} rebought` }));
    p.outs.forEach((t) => moments.push({ t, text: `${p.name} went out` }));
    p.backs.forEach((t) => moments.push({ t, text: `${p.name} came back in` }));
  }
  moments.push({ t: endedAt, text: "Game ended" });
  moments.sort((a, b) => a.t - b.t);

  const potAt = (t) => bands.reduce((s, b) => s + valueAt(b, t), 0);
  const playingAt = (t) => list.filter((p) => p.segments.some(([a, b]) => a <= t && t < b)).length || (t >= endedAt ? list.filter((p) => p.inAtEnd).length : 0);
  return { list, bands, times, pot, moments, valueAt, potAt, playingAt, start: startedAt, end: endedAt };
}

function useWidth() {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

// Snap a time to the nearest moment within `px` pixels, else keep it.
function snap(night, t, msPerPx, px = 14) {
  let best = null;
  for (const m of night.moments) {
    const d = Math.abs(m.t - t);
    if (d <= px * msPerPx && (!best || d < Math.abs(best.t - t))) best = m;
  }
  return best ? best.t : t;
}

function niceStep(max, target = 3) {
  const raw = max / target;
  const mag = 10 ** Math.floor(Math.log10(raw || 1));
  const n = raw / mag;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * mag;
}

// Clock marks on round times (15/30/60/120 min) at least `minGap` px apart.
function clockTicks(start, end, pxPerMs, minGap) {
  const step = [15, 30, 60, 120, 180].map((m) => m * 60000).find((s) => s * pxPerMs >= minGap) || 180 * 60000;
  const ticks = [];
  const first = Math.ceil(start / step) * step;
  for (let t = first; t < end; t += step) ticks.push(t);
  return ticks;
}

// ── Pot over time ─────────────────────────────────────────────────────
// The pot through the night as one stepped line. Drag (or tap) to read any
// moment; the readout above shows it.
export const PotChart = memo(function PotChart({ night, scrubT, onScrub, formatMoney }) {
  const [ref, width] = useWidth();
  const H = 190;
  const pad = { l: 8, r: 8, t: 20, b: 24 };
  const W = Math.max(width, 200);
  const iw = W - pad.l - pad.r;
  const ih = H - pad.t - pad.b;
  const span = Math.max(night.end - night.start, 60000);
  const x = (t) => pad.l + ((Math.min(Math.max(t, night.start), night.end) - night.start) / span) * iw;
  const yMax = Math.max(night.pot, 1);
  const step = niceStep(yMax);
  const top = Math.ceil(yMax / step) * step;
  const y = (v) => pad.t + ih - (v / top) * ih;

  // One line: the pot, stepping up at each buy-in, with a dot on each step.
  const line = useMemo(() => {
    const { times } = night;
    const vals = times.map((t) => night.potAt(t));
    let d = `M${x(times[0])},${y(vals[0])}`;
    for (let i = 1; i < times.length; i++) d += `H${x(times[i])}V${y(vals[i])}`;
    const area = `${d}V${y(0)}H${x(times[0])}Z`;
    const dots = times.map((t, i) => ({ t, v: vals[i] })).filter((p, i) => i > 0 ? p.v > vals[i - 1] : p.v > 0);
    return { d, area, dots };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [night, W]);

  const grid = [];
  for (let v = step; v <= top; v += step) grid.push(v);
  const ticks = clockTicks(night.start, night.end, iw / span, 64).filter((t) => x(t) - pad.l > 44 && W - pad.r - x(t) > 44);
  const msPerPx = span / iw;
  const scrubFrom = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    const t = night.start + ((e.clientX - box.left - pad.l) / iw) * span;
    onScrub(snap(night, Math.min(Math.max(t, night.start), night.end), msPerPx));
  };

  return (
    <div className="tld-pot" ref={ref}>
      {width > 0 && (
        <svg
          width={W}
          height={H}
          className="tld-pot-svg"
          role="img"
          aria-label={`Pot over time, from 0 to ${formatMoney(night.pot)}.`}
          onPointerDown={(e) => { e.currentTarget.setPointerCapture?.(e.pointerId); scrubFrom(e); }}
          onPointerMove={(e) => { if (e.buttons || e.pointerType === "mouse") scrubFrom(e); }}
        >
          {grid.map((v) => (
            <g key={v}>
              <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} className="tld-grid" />
              <text x={pad.l + 2} y={y(v) - 4} className="tld-axis-text">{formatMoney(v)}</text>
            </g>
          ))}
          <path d={line.area} className="tld-pot-area" />
          <path d={line.d} className="tld-pot-line" />
          {line.dots.map((p) => <circle key={p.t} cx={x(p.t)} cy={y(p.v)} r={3} className="tld-pot-dot" />)}
          <line x1={x(night.end)} x2={x(night.end)} y1={pad.t - 4} y2={pad.t + ih} className="tld-finish" />
          <line x1={pad.l} x2={W - pad.r} y1={pad.t + ih} y2={pad.t + ih} className="tld-baseline" />
          <text x={pad.l} y={H - 6} className="tld-axis-text">{clock(night.start)}</text>
          {ticks.map((t) => <text key={t} x={x(t)} y={H - 6} textAnchor="middle" className="tld-axis-text">{hourLabel(t)}</text>)}
          <text x={W - pad.r} y={H - 6} textAnchor="end" className="tld-axis-text tld-axis-text--strong">{clock(night.end)}</text>
          {scrubT !== null && (
            <g className="tld-scrub" pointerEvents="none">
              <line x1={x(scrubT)} x2={x(scrubT)} y1={pad.t - 4} y2={pad.t + ih} />
              <circle cx={x(scrubT)} cy={y(night.potAt(scrubT))} r={5} />
            </g>
          )}
        </svg>
      )}
    </div>
  );
});

// ── Metro map ─────────────────────────────────────────────────────────
// Time runs down the page (a phone's long side), one coloured line per
// player: ring when they sat down, square per rebuy (a number when several
// fall close together), × when they went out, a dot on the finish line for
// everyone still in at the end. Tap anywhere to mark that moment.
export const MetroChart = memo(function MetroChart({ night, scrubT, onScrub, renderAvatar, formatMoney }) {
  const [ref, width] = useWidth();
  const gutter = 64;
  const headH = 60;
  const footH = 30;
  const span = Math.max(night.end - night.start, 60000);
  const bodyH = Math.min(Math.max((span / 60000) * 2, 320), 1000);
  const H = headH + bodyH + footH;
  const W = Math.max(width, 240);
  const cols = night.list.length;
  const colW = (W - gutter - 8) / Math.max(cols, 1);
  const cx = (i) => gutter + colW * (i + 0.5);
  const y = (t) => headH + ((Math.min(Math.max(t, night.start), night.end) - night.start) / span) * bodyH;
  const ticks = clockTicks(night.start, night.end, bodyH / span, 46).filter((t) => y(t) - headH > 18 && headH + bodyH - y(t) > 18);
  const msPerPx = span / bodyH;
  const near = (t) => scrubT !== null && Math.abs(t - scrubT) < 60000;

  // Rebuys closer than a marker's height merge into one numbered marker.
  const groupRebuys = (times) => {
    const groups = [];
    for (const t of times) {
      const g = groups[groups.length - 1];
      if (g && y(t) - y(g.last) < 16) { g.n += 1; g.last = t; } else groups.push({ first: t, last: t, n: 1 });
    }
    return groups.map((g) => ({ ...g, t: (g.first + g.last) / 2 }));
  };

  const onTap = (e) => {
    const box = e.currentTarget.getBoundingClientRect();
    const t = night.start + ((e.clientY - box.top - headH) / bodyH) * span;
    if (t < night.start - 10 * msPerPx || t > night.end + 10 * msPerPx) { onScrub(null); return; }
    onScrub(snap(night, Math.min(Math.max(t, night.start), night.end), msPerPx));
  };
  const avatarSize = Math.min(28, Math.max(18, colW - 10));

  return (
    <div className="tld-metro" ref={ref}>
      {width > 0 && (
        <div className="tld-metro-inner" style={{ height: H }}>
          {/* Heads: avatar ringed in the player's colour, name under it. */}
          {night.list.map((p, i) => (
            <div key={p.pid} className={`tld-metro-head${p.inAtEnd ? "" : " is-out"}`} style={{ left: cx(i) - colW / 2, width: colW, "--c": slotColor(p.slot) }}>
              <span className="tld-metro-avatar" style={{ width: avatarSize + 4, height: avatarSize + 4 }}>{renderAvatar ? renderAvatar(p.pid, p.name, avatarSize) : null}</span>
              <span className="tld-metro-name">{p.name}</span>
            </div>
          ))}
          <svg width={W} height={H} className="tld-metro-svg" role="img" aria-label="Each player's night, top to bottom: when they sat down, each rebuy, and when they went out." onClick={onTap}>
            {ticks.map((t) => (
              <g key={t}>
                <line x1={gutter - 6} x2={W - 4} y1={y(t)} y2={y(t)} className="tld-grid" />
                <text x={gutter - 10} y={y(t) + 4} textAnchor="end" className="tld-axis-text">{hourLabel(t)}</text>
              </g>
            ))}
            <line x1={gutter - 6} x2={W - 4} y1={y(night.start)} y2={y(night.start)} className="tld-startline" />
            <text x={gutter - 10} y={y(night.start) + 4} textAnchor="end" className="tld-axis-text tld-axis-text--strong">{clock(night.start)}</text>
            <line x1={gutter - 6} x2={W - 4} y1={y(night.end)} y2={y(night.end)} className="tld-finish" />
            <text x={gutter - 10} y={y(night.end) + 4} textAnchor="end" className="tld-axis-text tld-axis-text--strong">{clock(night.end)}</text>
            <text x={W - 4} y={y(night.end) + 20} textAnchor="end" className="tld-axis-text">Game ended</text>

            {night.list.map((p, i) => {
              const c = slotColor(p.slot);
              const x = cx(i);
              return (
                <g key={p.pid} style={{ "--c": c }} className={p.inAtEnd ? "" : "is-out"}>
                  {p.segments.map(([a, b]) => (
                    <line key={a} x1={x} x2={x} y1={y(a)} y2={y(b)} className="tld-metro-line" />
                  ))}
                  <circle cx={x} cy={y(p.seat)} r={near(p.seat) ? 7 : 5.5} className="tld-metro-seat" />
                  {p.backs.map((t) => <circle key={`b${t}`} cx={x} cy={y(t)} r={near(t) ? 7 : 5.5} className="tld-metro-seat" />)}
                  {groupRebuys(p.rebuys).map((g) => {
                    const big = near(g.first) || near(g.last);
                    const s = big ? 16 : 13;
                    return (
                      <g key={g.first} transform={`translate(${x},${y(g.t)})`}>
                        <rect x={-s / 2} y={-s / 2} width={s} height={s} rx={4} className="tld-metro-rebuy" />
                        {g.n > 1
                          ? <text y={4} textAnchor="middle" className="tld-metro-count">{g.n}</text>
                          : <path d="M-3.5,0H3.5M0,-3.5V3.5" className="tld-metro-plus" />}
                      </g>
                    );
                  })}
                  {p.outs.map((t) => (
                    <g key={`o${t}`} transform={`translate(${x},${y(t)})`} className="tld-metro-out">
                      <circle r={near(t) ? 9 : 7.5} />
                      <path d="M-3.5,-3.5L3.5,3.5M3.5,-3.5L-3.5,3.5" />
                    </g>
                  ))}
                  {p.inAtEnd && <circle cx={x} cy={y(night.end)} r={5.5} className="tld-metro-end" />}
                </g>
              );
            })}

            {scrubT !== null && (
              <g className="tld-scrub" pointerEvents="none">
                <line x1={gutter - 6} x2={W - 4} y1={y(scrubT)} y2={y(scrubT)} />
                <rect x={2} y={y(scrubT) - 10} width={gutter - 6} height={20} rx={10} className="tld-scrub-pill" />
                <text x={(gutter - 4) / 2 + 1} y={y(scrubT) + 4} textAnchor="middle" className="tld-scrub-text">{clock(scrubT)}</text>
              </g>
            )}
          </svg>
          {scrubT !== null && (
            <div className="tld-metro-tag" style={{ top: Math.max(headH, y(scrubT) - 30) }}>
              {formatMoney(night.potAt(scrubT))} pot · {night.playingAt(scrubT)} playing
            </div>
          )}
        </div>
      )}
    </div>
  );
});
